import classnames from 'classnames';
import { parseISO } from 'date-fns';
import isSameMonth from 'date-fns/isSameMonth';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { forEach } from 'lib/helpers';
import _isEqual from 'lodash/isEqual';
import _some from 'lodash/some';
import _uniqWith from 'lodash/uniqWith';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { START_DATE } from 'contexts/CruiseSearchContext/constants';
import Carousel, { CAROUSEL_ITEM_CLASS } from '../Carousel';

import { getDateFilters, makeInitialRangesFromStartDate, monthsAvailable } from '../../FiltersModal/filters/Dates/utils';

import { getDisableMonth } from 'lib/promotions';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { MONTHS, MONTHS_WRAP, MONTH_BUTTON } from './constants';
import { Calendar, MonthButton, MonthTile, MonthsWrapper, YearLabel, YearWrapper } from './styles';

/**
 * Months is a controlled calendar component.
 * Displays months (grouped by year) within the startDate facets returned from the graph.
 * Sets/listens to the startDate property of searchSelecting.
 * @param {array} range Controlled start, end range value objects
 * @param {function} onChange Controlled onChange handler
 * @param {number} currentIndex Used to control the carousel when screen size above small
 * @param {function} setCurrentIndex Function controlling carousel index
 * @param {string} scrollerId The id of the component's parent scroller
 *
 * @param mockCalendar Used for stories/testing out of context
 */
function Months({ isMobile, mockCalendar, active, monthsList, tabs, monthsRef, selectCustom }) {
	const {
		monthsCalendar = [],
		facetOptions,
		updateSearchSelectingFilter,
		updateMonthsSelectingFilter,
		setAppliedDateFilter,
		monthsSelecting,
		dateRangeCalendar,
		fetchAndUpdateFacetOptions,
		brand,
		promotions,
		promotionSelected,
		getUniqueDestinationsState,
		uniqueDestinations,
		filterOptionsDictionary,
	} = useContext(CruiseSearchContext);
	const { uiSettings } = getBrandUISettings(brand);
	const calendar = mockCalendar || monthsCalendar;
	const today = useRef(new Date());

	const classes = classnames({
		active,
		[MONTHS]: true,
	});

	const getDateRangeGroups = (date) => {
		const datesRange = [];

		forEach(date, (item) => {
			let startCode, endCode;

			if (typeof item === 'string') {
				[startCode, endCode] = item.split('~');
			} else {
				startCode = item.min;
				endCode = item.max;
			}

			if (!isNaN(Date.parse(startCode)) && !isNaN(Date.parse(endCode))) {
				const [yearStart, monthStart] = startCode.split('-');
				const [yearEnd, monthEnd] = endCode.split('-');
				const startDateUTC = new Date(yearStart, parseInt(monthStart) - 1, 1, 0, 0, 0);
				const endDateUTC = new Date(yearEnd, parseInt(monthEnd), 1, 0, 0, 0);

				const datesAvailable = dateRangeCalendar.filter((item) => {
					const startOfMonth = item.startOfMonth && !isNaN(Date.parse(item.startOfMonth));
					const endOfMonth = item.endOfMonth && !isNaN(Date.parse(item.endOfMonth));

					if (startOfMonth && endOfMonth) {
						const startDateCalendarUTC = new Date(`${item.startOfMonth}T00:00:00`);
						const endDateCalendarUTC = new Date(`${item.endOfMonth}T00:00:00`);
						return startDateCalendarUTC >= startDateUTC && endDateCalendarUTC <= endDateUTC;
					}
				});
				forEach(datesAvailable, (date) => datesRange.push(`${date.startOfMonth}~${date.endOfMonth}`));
			}
		});

		return datesRange;
	};

	const getCountFacet = (monthCode) => facetOptions[START_DATE]?.[monthCode]?.count;
	const startDate = monthsSelecting[START_DATE];
	const dateRanges = getDateRangeGroups(startDate);
	const { initialMonthsRange, initialMonthsIndex } = useMemo(
		() => {
			let dates = monthsAvailable(monthsCalendar, today, facetOptions);
			const { initialMonthsRange, initialMonthsIndex } = makeInitialRangesFromStartDate(dateRanges, dateRangeCalendar, dates);
			return { initialMonthsRange: _uniqWith(initialMonthsRange, _isEqual), initialMonthsIndex };
		},
		[dateRangeCalendar, facetOptions], // eslint-disable-line react-hooks/exhaustive-deps
	);

	const [monthsRange, setMonthsRange] = useState(initialMonthsRange);
	const [monthsRangeStart, monthsRangeEnd] = monthsRange;
	const [currentMonthsIndex, setCurrentMonthsIndex] = useState(initialMonthsIndex);

	const updateSearchFilters = (dateFilter, callback) => {
		updateMonthsSelectingFilter(START_DATE, dateFilter);
		updateSearchSelectingFilter(START_DATE, dateFilter, callback);
	};

	const onMonthsChange = (day) => {
		let nextRange = [...monthsRange];

		if (monthsRange.filter((item) => item?.[0]?.code === day.code).length) {
			nextRange = monthsRange.filter((item) => item?.[0]?.code !== day.code);
		} else {
			nextRange.push([day]);
			nextRange = nextRange.sort((a, b) => a.date - b.date);
		}

		let dateFilter = getDateFilters(nextRange, ['startOfMonth', 'endOfMonth'], [0]);
		const { initialMonthsRange } = makeInitialRangesFromStartDate(dateFilter, dateRangeCalendar);
		let uniqueAplied = null;
		const keyUnique = uniqueDestinations?.length && Object.keys(uniqueDestinations[0] || {})?.[0];

		const { custom } = filterOptionsDictionary || {};
		const { options = [] } = custom || {};
		if (keyUnique) {
			uniqueAplied = options.filter((item) => item.code === keyUnique)?.[0];
		} else {
			const specialFilters = getUniqueDestinationsState();
			uniqueAplied = specialFilters.filter((item) => item.selected)?.[0];
		}

		if (uniqueAplied?.code) {
			const query = uniqueAplied?.criteria || {};
			if (!query?.promo) {
				const { [START_DATE]: dates } = query;
				const groupDates = getDateRangeGroups(dates);
				const equalDates = groupDates.filter((a) => _some(dateFilter, (b) => _isEqual(a, b)));

				const restDates = dateFilter.filter((value) => !equalDates.includes(value));
				const selected = _isEqual(equalDates, groupDates);

				if (selected) {
					dateFilter = [...equalDates, ...restDates];
				}
			}
		}

		nextRange = initialMonthsRange;

		setMonthsRange(nextRange);
		updateSearchFilters(dateFilter, fetchAndUpdateFacetOptions);
		setAppliedDateFilter(tabs.months.appliedDateFilter);
	};

	const getCustomFilters = () => {
		const monthsRangeToUpdate = [];
		forEach(startDate, (date) => {
			const [startDateFilter, endDateFilter] = date ? date.split('~') : [];
			if ((startDateFilter, endDateFilter)) {
				monthsRangeToUpdate.push([
					{ date: startDateFilter, code: startDateFilter },
					{ date: endDateFilter, code: endDateFilter },
				]);
			}
		});
		const dateFilter = getDateFilters(monthsRangeToUpdate, ['code'], [0, 1]);
		const dateRanges = getDateRangeGroups(dateFilter);
		const { initialMonthsRange } = makeInitialRangesFromStartDate(dateRanges, dateRangeCalendar);
		if (JSON.stringify(initialMonthsRange) !== JSON.stringify(monthsRange)) {
			setMonthsRange(initialMonthsRange);
		}
	};

	useEffect(() => {
		if (selectCustom !== null) {
			getCustomFilters();
		}
	}, [selectCustom, monthsList]);

	useEffect(() => {
		const noStart = !monthsRangeStart?.[0]?.startOfMonth;
		const noEnd = !monthsRangeStart?.[0]?.endOfMonth && !monthsRangeEnd?.[0]?.endOfMonth;
		if (noStart && noEnd) {
			updateSearchFilters([], fetchAndUpdateFacetOptions);
		}
	}, [monthsRange]); // eslint-disable-line react-hooks/exhaustive-deps

	const onResetMonths = () => {
		setMonthsRange([]);
	};

	useEffect(() => {
		monthsRef.reset = onResetMonths;
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	let count = 0;
	return (
		<Calendar className={classes}>
			<Carousel currentIndex={currentMonthsIndex} setCurrentIndex={setCurrentMonthsIndex} isMobile={isMobile}>
				{Object.keys(calendar).map((year) => {
					const months = calendar[year];
					const index = count;
					count += 1;

					return (
						<YearWrapper key={year} index={index} className={CAROUSEL_ITEM_CLASS}>
							<YearLabel className="yearLabel" title={year} />
							<MonthsWrapper className={MONTHS_WRAP} gap={uiSettings.filtersModal.monthsGrid.gap}>
								{months.map((month) => {
									const { monthsLabel, date } = month;
									const unavailable = date < today?.current && !isSameMonth(parseISO(date), today?.current);
									let disabled = month?.disabled || unavailable;

									disabled = getCountFacet(month?.code) === 0 ? true : disabled;

									const disabledFacet = disabled ? false : getCountFacet(month?.code) <= 0;
									const selected = _some(monthsRange, (group) => {
										const [start, end] = group;
										return date === start?.date || date === end?.date || (date > start?.date && date < end?.date && getCountFacet(month?.code) > 0);
									});

									const buttonClasses = classnames({
										selected,
										// disabled,
										disabled: getDisableMonth({ promotions, month, disabled, promotionSelected }),
										'disabled-facet': selected ? false : disabledFacet,
										[MONTH_BUTTON]: true,
										//unclickable: isSpecialFilter || !!promotions?.valid,
									});
									return (
										<MonthTile key={`${year}-${monthsLabel}`}>
											<MonthButton
												className={buttonClasses}
												onClick={() => onMonthsChange(month)}
												selected={selected}
												maxWidth={uiSettings.filtersModal.monthsButton.maxWidth}
											>
												{monthsLabel}
											</MonthButton>
										</MonthTile>
									);
								})}
							</MonthsWrapper>
						</YearWrapper>
					);
				})}
			</Carousel>
		</Calendar>
	);
}

Months.propTypes = {
	isMobile: PropTypes.bool.isRequired,
	mockCalendar: PropTypes.object,
	active: PropTypes.bool,
	monthsList: PropTypes.array,
	tabs: PropTypes.object,
	monthsRef: PropTypes.object.isRequired,
	selectCustom: PropTypes.bool,
};

export default Months;
